import { useEffect, useRef, useState } from 'react'

import { graph } from '../architecture.js'

// Fig 1 — real Mermaid, themed to the tokens rather than left on its defaults.
//
// Bundled, not loaded from a CDN. The handoff says "Mermaid 11 via CDN in the
// prototype; use whatever the build prefers", and a PWA with an offline shell
// prefers not to depend on a third-party host at render time.
//
// It is a DYNAMIC import on purpose: mermaid is by far the largest dependency
// here, and the landing page must not pay for a diagram it does not show. The
// import creates its own chunk, fetched only when this component mounts, and
// the service worker caches it on first use like any other hashed asset — so
// it is not in the shell precache and does not inflate a cold first load.
//
// themeVariables come from the real tokens, read off the document at render
// time, so the diagram cannot drift from the palette the rest of the page uses.

// 🔴 THE GRAPH IS NO LONGER WRITTEN HERE, AND THAT IS THE WHOLE OF P6-107.
//
// It used to be a template string in this file, and it drew a system that did
// not exist: EIGHT nodes, THREE of them the retired trivia Activity, with the
// website's only edge a dotted line to the trivia service. `music/`, `trips/`
// and the bot's directory publish had never appeared at all. A template string
// rots the day a deployable lands or leaves, which has now happened twice in a
// month, in both directions.
//
// ../architecture.js holds the model, and src/test/architecture.test.js derives
// what must be in it from the repository — every Dockerfile is a node, every
// cog in bot.py's COGS is drawn or explicitly not, and every node names paths
// that exist. Read that file's header before editing the picture.
//
// ✅ The `linkStyle` index hazard this comment used to warn about is GONE
// rather than documented: the indices are computed from the model's `critical`
// flags, and the gate asserts the emitted lines against them. Adding an edge in
// the middle no longer makes the legend describe the wrong arrows.

function tokenValue(name, fallback) {
  if (typeof getComputedStyle !== 'function') return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value || fallback
}

export default function Diagram({ caption }) {
  const hostRef = useRef(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function render() {
      try {
        const { default: mermaid } = await import('mermaid')
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: 'base',
          fontFamily: tokenValue('--d2d-font-ui', 'sans-serif'),
          themeVariables: {
            background: tokenValue('--d2d-surface', '#0b0e11'),
            primaryColor: tokenValue('--d2d-surface-raised', '#15191f'),
            primaryTextColor: tokenValue('--d2d-text', '#eaeef2'),
            primaryBorderColor: tokenValue('--d2d-border', '#333c47'),
            lineColor: tokenValue('--d2d-border-hover', '#48535f'),
            secondaryColor: tokenValue('--d2d-surface', '#0b0e11'),
            tertiaryColor: tokenValue('--d2d-surface', '#0b0e11'),
            fontSize: '14px',
          },
        })
        const { svg } = await mermaid.render(
          `d2d-fig-1-${Date.now()}`,
          graph(tokenValue('--d2d-accent', '#ff9455'), tokenValue('--d2d-border-hover', '#48535f')),
        )
        if (!cancelled && hostRef.current) hostRef.current.innerHTML = svg
      } catch {
        // A diagram that fails to render must not take the page with it. The
        // prose above it is the actual content; the figure is an illustration.
        if (!cancelled) setFailed(true)
      }
    }

    render()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <figure className="d2d-figure">
      <div className="d2d-figure__head">
        <figcaption className="d2d-micro">{caption}</figcaption>
        <span className="d2d-figure__legend d2d-micro">
          <span className="d2d-figure__key">
            <span className="d2d-figure__swatch" aria-hidden="true" /> critical path
          </span>
          <span className="d2d-figure__key">
            <span className="d2d-figure__swatch d2d-figure__swatch--quiet" aria-hidden="true" />{' '}
            everything else
          </span>
        </span>
      </div>

      <div className="d2d-figure__scroll">
        <div className="d2d-figure__canvas" ref={hostRef} data-testid="diagram-canvas" />
        {failed ? (
          <p data-testid="diagram-fallback">
            The diagram could not be drawn. The section above describes the same thing in words.
          </p>
        ) : null}
      </div>

      {/* On a phone the figure is a horizontally scrollable strip — shrinking a
          flowchart to 350px is what makes these unreadable. */}
      <p className="d2d-figure__note d2d-micro">Swipe the diagram sideways on a small screen</p>
    </figure>
  )
}
